import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import * as JsSIP from "jssip";
import Swal from 'sweetalert2';
import { useNoiseSuppression } from '../hooks/useNoiseSuppression';
import useSocket from '../hooks/useWebsocket';

const SIPContext = createContext();

export const SIPProvider = ({ children }) => {
    const [val] = useSocket();
    const { getProcessedStream, setNoiseCancellation, cleanup: cleanupNoise } = useNoiseSuppression();
    const cleanupNoiseRef = useRef(cleanupNoise);
    useEffect(() => { cleanupNoiseRef.current = cleanupNoise; }, [cleanupNoise]);

    const [isNoiseCancellationEnabled, setIsNoiseCancellationEnabled] = useState(() =>
        localStorage.getItem('noiseCancellationEnabled') !== 'false'
    );
    const isNoiseCancellationEnabledRef = useRef(isNoiseCancellationEnabled);
    useEffect(() => {
        isNoiseCancellationEnabledRef.current = isNoiseCancellationEnabled;
        localStorage.setItem('noiseCancellationEnabled', isNoiseCancellationEnabled);
    }, [isNoiseCancellationEnabled]);

    const [session, setSession] = useState(null);
    const [config, setConfig] = useState(() => {
        const saved = localStorage.getItem('sipConfig');
        return saved ? JSON.parse(saved) : {
            username: process.env.REACT_APP_SIP_USERNAME || '',
            password: process.env.REACT_APP_SIP_PASSWORD || '',
            domain: process.env.REACT_APP_SIP_DOMAIN || '',
            webrtcUrl: process.env.REACT_APP_WEBRTC_URL || ''
        };
    });

    // Microphone permission state
    const [micPermission, setMicPermission] = useState('prompt'); // 'granted' | 'denied' | 'prompt'
    const [permissionError, setPermissionError] = useState('');

    const [audioDevices, setAudioDevices] = useState({
        microphones: [],
        speakers: []
    });
    const [selectedDevices, setSelectedDevices] = useState({
        microphone: 'default',
        speaker: 'default'
    });

    const checkMicrophonePermission = useCallback(async () => {
        setPermissionError('');
        try {
            if (navigator.permissions && navigator.permissions.query) {
                const result = await navigator.permissions.query({ name: 'microphone' });
                setMicPermission(result.state);
                result.onchange = () => {
                    setMicPermission(result.state);
                };
                return result.state;
            } else {
                setMicPermission('prompt');
                return 'prompt';
            }
        } catch (error) {
            console.error('Error checking microphone permission:', error);
            setPermissionError(error.message);
            return 'prompt';
        }
    }, []);

    const requestMicrophonePermission = useCallback(async () => {
        setPermissionError('');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setMicPermission('granted');
            stream.getTracks().forEach(track => track.stop());
            return true;
        } catch (error) {
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                setMicPermission('denied');
                setPermissionError('Microphone access denied by user');
            } else if (error.name === 'NotFoundError') {
                setMicPermission('denied');
                setPermissionError('No microphone found on this device');
            } else {
                setPermissionError(`Error: ${error.message}`);
            }
            console.error('Error requesting microphone permission:', error);
            return false;
        }
    }, []);

    const getAudioDevices = useCallback(async () => {
        try {
            // Only enumerate devices if permission is already granted.
            // Never call getUserMedia here — it must only be triggered by explicit user actions.
            if (micPermission !== 'granted') return;

            const devices = await navigator.mediaDevices.enumerateDevices();
            const microphones = devices.filter(device => device.kind === 'audioinput');
            const speakers = devices.filter(device => device.kind === 'audiooutput');

            setAudioDevices({ microphones, speakers });

            if (!selectedDevices.speaker && speakers.length > 0) {
                const builtInSpeaker = speakers.find(s =>
                    s.label.toLowerCase().includes('built-in') ||
                    s.label.toLowerCase().includes('internal') ||
                    (!s.label.toLowerCase().includes('headphone') &&
                        !s.label.toLowerCase().includes('bluetooth'))
                );

                const preferredSpeaker = builtInSpeaker || speakers[0];

                setSelectedDevices(prev => ({
                    ...prev,
                    speaker: preferredSpeaker.deviceId
                }));

                if (remoteAudioRef.current && typeof remoteAudioRef.current.setSinkId === 'function') {
                    remoteAudioRef.current.setSinkId(preferredSpeaker.deviceId).catch(console.error);
                }
            }

            console.log('Found microphones:', microphones);
            console.log('Found speakers:', speakers);
        } catch (error) {
            console.error('Error getting audio devices:', error);
        }
    }, [selectedDevices, micPermission]);

    const changeMicrophone = useCallback(async (deviceId) => {
        console.log('Changing microphone to:', deviceId);
        setSelectedDevices(prev => ({ ...prev, microphone: deviceId }));

        if (session && session.connection) {
            try {
                const micId = deviceId === 'default' ? '' : deviceId;
                const newStream = await getProcessedStream(micId, isNoiseCancellationEnabledRef.current);
                const audioTrack = newStream.getAudioTracks()[0];
                const sender = session.connection.getSenders().find(s => s.track?.kind === 'audio');

                if (sender) {
                    await sender.replaceTrack(audioTrack);
                    console.log('Microphone changed successfully');
                }
            } catch (error) {
                console.error('Error changing microphone:', error);
            }
        }
    }, [session, getProcessedStream]);

    const changeSpeaker = useCallback(async (deviceId) => {
        console.log('Changing speaker to:', deviceId);
        setSelectedDevices(prev => ({ ...prev, speaker: deviceId }));

        if (remoteAudioRef.current && typeof remoteAudioRef.current.setSinkId === 'function') {
            try {
                await remoteAudioRef.current.setSinkId(deviceId);
                console.log('Speaker changed successfully');
            } catch (error) {
                console.error('Error changing speaker:', error);
            }
        }
    }, []);

    // Check permission on mount; enumerate devices only if already granted.
    useEffect(() => {
        const init = async () => {
            const status = await checkMicrophonePermission();
            if (status === 'granted') {
                getAudioDevices();
            }
        };
        init();
        navigator.mediaDevices.addEventListener('devicechange', getAudioDevices);
        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', getAudioDevices);
        };
    }, [getAudioDevices, checkMicrophonePermission]);

    // When permission becomes granted (e.g. after requestMicrophonePermission), refresh devices.
    useEffect(() => {
        if (micPermission === 'granted') {
            getAudioDevices();
        }
    }, [micPermission, getAudioDevices]);

    React.useEffect(() => {
        localStorage.setItem('sipConfig', JSON.stringify(config));
    }, [config]);

    const [status, setStatus] = useState('disconnected');
    const [callStatus, setCallStatus] = useState('idle');
    const [caller, setCaller] = useState('');
    const [callDirection, setCallDirection] = useState(null); // 'incoming' | 'outgoing' | null
    // Populated by the dialer (Call.js) whenever the active call is a campaign call
    // (has a uid/camp_details), so the webrtc call popup knows whether to offer feedback.
    const [campaignCallInfo, setCampaignCallInfo] = useState(null);
    const [callerNumber, setCallerNumber] = useState('');
    const [isHeld, setIsHeld] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    // eventId of the follow-up/report row a call was dialed from (if any) - lets the
    // active-call Feedback popup pull up that call's existing feedback to edit.
    const [callEventId, setCallEventId] = useState('');
    // Single source of truth for the header dialpad icon — lets the one
    // app-wide call popup double as the outbound dialer instead of opening a second popup.
    const [isDialerOpen, setIsDialerOpen] = useState(false);
    const openDialer = useCallback(() => setIsDialerOpen(true), []);
    const closeDialer = useCallback(() => setIsDialerOpen(false), []);

    const uaRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const ringtoneCtxRef = useRef(null);
    const ringtoneIntervalRef = useRef(null);
    const sessionRef = useRef(null);
    const timerRef = useRef(null);

    const configRef = useRef(config);

    const updateConfig = useCallback((newConfig) => {
        configRef.current = newConfig;
        setConfig(newConfig);
    }, []);

    const playRingtone = useCallback(() => {
        if (ringtoneCtxRef.current) {
            ringtoneCtxRef.current.close();
            ringtoneCtxRef.current = null;
        }
        clearInterval(ringtoneIntervalRef.current);

        const australianRing = (ctx, startTime) => {
            const ringPulse = (start) => {
                const osc1 = ctx.createOscillator();
                const osc2 = ctx.createOscillator();
                const gain = ctx.createGain();

                osc1.connect(gain);
                osc2.connect(gain);
                gain.connect(ctx.destination);

                osc1.type = 'sine';
                osc2.type = 'sine';
                osc1.frequency.value = 413;
                osc2.frequency.value = 438;

                gain.gain.setValueAtTime(0.3, start);
                gain.gain.setValueAtTime(0.3, start + 0.4);
                gain.gain.linearRampToValueAtTime(0, start + 0.45);

                osc1.start(start);
                osc2.start(start);
                osc1.stop(start + 0.45);
                osc2.stop(start + 0.45);
            };

            ringPulse(startTime);
            ringPulse(startTime + 0.6);
        };

        const ring = () => {
            if (ringtoneCtxRef.current) {
                ringtoneCtxRef.current.close().catch(() => {});
            }
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            ringtoneCtxRef.current = ctx;
            australianRing(ctx, ctx.currentTime);
        };

        ring();
        ringtoneIntervalRef.current = setInterval(ring, 3000);
    }, []);

    const stopRingtone = useCallback(() => {
        clearInterval(ringtoneIntervalRef.current);
        ringtoneIntervalRef.current = null;
        if (ringtoneCtxRef.current) {
            ringtoneCtxRef.current.close();
            ringtoneCtxRef.current = null;
        }
    }, []);

    const playCallEndSound = useCallback(() => {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const now = ctx.currentTime;

        // Dual-tone beep - mirrors the classic telephone "call disconnected" signal
        const dualBeep = (start, duration) => {
            [950, 1400].forEach((freq) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.type = 'sine';
                osc.frequency.value = freq;

                gain.gain.setValueAtTime(0.0001, start);
                gain.gain.exponentialRampToValueAtTime(0.12, start + 0.015);
                gain.gain.setValueAtTime(0.12, start + duration - 0.03);
                gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

                osc.start(start);
                osc.stop(start + duration);
            });
        };

        // Two short beeps, like a call disconnecting
        dualBeep(now, 0.18);
        dualBeep(now + 0.28, 0.18);

        setTimeout(() => ctx.close().catch(() => {}), 800);
    }, []);

    useEffect(() => {
        if (callStatus === 'incoming') {
            playRingtone();
        } else {
            stopRingtone();
        }
        return () => stopRingtone();
    }, [callStatus, playRingtone, stopRingtone]);

    const teardownUa = useCallback(() => {
        if (uaRef.current) {
            try {
                uaRef.current.removeAllListeners();
                uaRef.current.stop();
            } catch (err) {
                console.warn('Error tearing down UA:', err);
            }
            uaRef.current = null;
        }
        setStatus('disconnected');
    }, []);

    const connect = useCallback((overrideConfig) => {
        const cfg = overrideConfig || configRef.current;
        console.log(cfg.webrtcUrl, "--------", cfg.username, "-------", cfg.password, "-------", cfg.domain)
        if (!cfg.webrtcUrl || !cfg.username || !cfg.password || !cfg.domain) {
            console.warn('SIP config incomplete, cannot connect');
            return;
        }

        if (uaRef.current && uaRef.current.isConnected() && uaRef.current.isRegistered()) {
            console.log('UA already connected and registered, skipping');
            return;
        }

        if (uaRef.current) {
            try {
                uaRef.current.removeAllListeners();
                uaRef.current.stop();
            } catch (err) {
                console.warn('Error stopping previous UA:', err);
            }
            uaRef.current = null;
        }

        try {
            const socket = new JsSIP.WebSocketInterface(cfg.webrtcUrl);
            const configuration = {
                sockets: [socket],
                uri: `sip:${cfg.username}@${cfg.domain}`,
                password: cfg.password,
                display_name: cfg.username,
                pcConfig: {
                    iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }],
                    iceTransportPolicy: 'all',
                    rtcpMuxPolicy: 'require',
                    bundlePolicy: 'max-bundle',
                },
                rtcOfferConstraints: {
                    offerToReceiveAudio: 1,
                    offerToReceiveVideo: 0,
                },
                sessionTimers: false,
                register: true,
                hackIpInContact: true,
                hackViaTcp: true,
                connection_recovery_min_interval: 2,
                connection_recovery_max_interval: 30
            };

            console.log('SIP auth attempt:', {
                uri: configuration.uri,
                passwordLength: configuration.password?.length,
                passwordFirstChar: configuration.password?.[0]
            });

            const newUa = new JsSIP.UA(configuration);
            uaRef.current = newUa;

            console.log('============  newUa  =============');
            console.log(newUa);
            console.log('============  newUa  =============');
            console.log(Object.keys(newUa));

            Object.keys(newUa).forEach(key => {
                console.log(key, newUa[key]);
            });
            console.log('============  newUa  =============');

            // Ignore events from a UA instance that connect() has since replaced/stopped -
            // JsSIP keeps emitting teardown events (disconnected/unregistered) asynchronously
            // after .stop(), and without this guard they overwrite status set by the new UA,
            // which re-triggers Header.js's auto-reconnect effect and causes an infinite
            // register/unregister loop against the SIP server.
            const isCurrent = () => uaRef.current === newUa;

            newUa.on('connecting', () => { if (!isCurrent()) return; console.log('UA connecting'); setStatus('connecting'); });
            newUa.on('connected', () => { if (!isCurrent()) return; console.log('UA connected'); setStatus('connected'); });
            newUa.on('disconnected', () => { if (!isCurrent()) return; console.log('UA disconnected'); setStatus('disconnected'); });
            newUa.on('registered', () => { if (!isCurrent()) return; console.log('UA registered'); setStatus('connected'); });
            newUa.on('unregistered', () => {
                if (!isCurrent()) return;
                console.log('UA unregistered');
                setStatus(newUa.isConnected() ? 'connecting' : 'disconnected');
            });
            newUa.on('registrationFailed', (e) => {
                if (!isCurrent()) return;
                console.warn('Registration failed:', {
                    cause: e.cause,
                    status: e.response?.status_code,
                    reason: e.response?.reason_phrase,
                    body: e.response?.body
                });
                setStatus(newUa.isConnected() ? 'connected' : 'disconnected');

                Swal.fire({
                    title: "Registration Failed",
                    text: "",
                    confirmButtonColor: "#3085d6",
                    icon: 'warning'
                });
                return;
            });

            newUa.on('newRTCSession', (data) => {
                if (!isCurrent()) return;
                const newSession = data.session;
                console.log("newSession     :  ",newSession,"---------data  : ",data)
                console.log('📞 New RTC Session:', {
                    direction: newSession.direction,
                    currentSession: !!sessionRef.current,
                    from: newSession.remote_identity.uri.user
                });

                if (newSession.direction === 'incoming') {
                    if (sessionRef.current) {
                        console.log('❌ BLOCKING incoming call - Agent already on a call');
                        newSession.terminate({
                            status_code: 486,
                            reason_phrase: 'Busy Here'
                        });
                        return;
                    }

                    console.log('✅ ACCEPTING incoming call from:', newSession.remote_identity.display_name);
                    setCallStatus('incoming');
                    setCallDirection('incoming');
                    // const incomingNumber = localStorage.getItem('customerNumber') || newSession.remote_identity.display_name || newSession.remote_identity.uri.user;
                    // console.log("===========  incomingNumber  :  ",incomingNumber)
                    // setCallerNumber(incomingNumber);
                    // setCaller(incomingNumber);
                    console.log('✅ ACCEPTING incoming call from:', newSession.remote_identity.uri.user);
                    setCallStatus('incoming');
                    setCaller(newSession.remote_identity.display_name || newSession.remote_identity.uri.user);
                } else {
                    // ✅ OUTGOING CALL (e.g. dialpad) - set caller to the number being dialed
                    setCallDirection('outgoing');
                    setCaller(newSession.remote_identity.display_name || newSession.remote_identity.uri.user);
                    const incomingNumber = newSession.remote_identity.uri.user;
                    const incomingName = newSession.remote_identity.display_name || incomingNumber;
                    setCallerNumber(incomingNumber);
                    setCaller(incomingName);
                }

                sessionRef.current = newSession;
                setSession(newSession);

                let attachDebounceTimer = null;
                const attachRemoteStream = () => {
                    console.log("attachRemoteStream Entered");
                    clearTimeout(attachDebounceTimer);
                    attachDebounceTimer = setTimeout(() => {
                        const liveSession = sessionRef.current;

                        if (!liveSession?.connection || !remoteAudioRef.current) {
                            console.warn('EXIT - connection:', liveSession?.connection, '| audioRef:', remoteAudioRef.current);
                            return;
                        }

                        const streams = liveSession.connection.getRemoteStreams();
                        const remoteStream = streams[0];
                        if (!remoteStream) return;

                        const audioEl = remoteAudioRef.current;
                        if (audioEl.srcObject === remoteStream) {
                            audioEl.play().catch(console.error);
                            return;
                        }

                        console.log('Attaching remote stream, tracks:', remoteStream.getTracks().length);
                        audioEl.srcObject = remoteStream;
                        audioEl.play().catch(err => console.error('Play error:', err));
                    }, 100);
                };

                newSession.on('peerconnection', (data) => {
                    const pc = data.peerconnection;
                    pc.addEventListener('track', (e) => {
                        if (e.track.kind === 'audio') attachRemoteStream();
                    });
                    pc.addEventListener('addstream', () => attachRemoteStream());
                });

                newSession.on('progress', (e) => {
                    console.log('Call progress - early media possible');
                    if (e.originator === 'remote') {
                        attachRemoteStream();
                    }
                });

                newSession.on('hold', () => setIsHeld(true));
                newSession.on('unhold', () => { setIsHeld(false); setTimeout(attachRemoteStream, 500); });
                newSession.on('connecting', () => setCallStatus('calling'));
                newSession.on('accepted', () => { setCallStatus('active'); setTimeout(attachRemoteStream, 100); });

                const resetCall = () => {
                    console.log('🔄 Resetting call state');
                    cleanupNoiseRef.current?.();
                    sessionRef.current = null;
                    setCallStatus('idle');
                    setCallDirection(null);
                    setSession(null);
                    setIsHeld(false);
                    setIsMuted(false);
                    setCallEventId('');
                    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
                    playCallEndSound();
                };

                newSession.on('sdp', (e) => {
                    console.log(`📄 ${e.originator === 'local' ? 'Local' : 'Remote'} SDP ${e.type}:\n${e.sdp}`);
                });

                newSession.on('ended', resetCall);
                newSession.on('failed', (e) => {
                    console.log('Call failed:', e.cause);

                    if (e.cause === 'User Denied Media Access') {
                        Swal.fire({
                            icon: 'warning',
                            title: 'Microphone Permission Required',
                            text: 'Please enable microphone access to make or receive calls.',
                            confirmButtonText: 'OK'
                        });
                    }

                    if (e.message) {
                        console.log('SIP failure response:', {
                            status_code: e.message.status_code,
                            reason_phrase: e.message.reason_phrase,
                            warning: e.message.getHeader?.('Warning'),
                            reason_header: e.message.getHeader?.('Reason'),
                        });
                    }
                    resetCall();
                });
            });

            newUa.start();
        } catch (error) {
            console.error('Failed to initialize JsSIP:', error);
            setStatus('disconnected');
        }
    }, []);

    useEffect(() => {
        return () => {
            if (uaRef.current) {
                uaRef.current.stop();
                uaRef.current = null;
            }
        };
    }, []);

    const disableSip = useCallback(() => {
        teardownUa();
    }, [teardownUa]);

    const makeCall = useCallback(async (number, eventId = '') => {
        // Request mic permission first — must happen before any early-return so the
        // browser dialog is always triggered from the user-gesture call stack.
        if (micPermission !== 'granted') {
            const granted = await requestMicrophonePermission();
            if (!granted) {
                Swal.fire({
                    title: 'Microphone Access Required',
                    text: 'Please allow microphone access in your browser to make calls.',
                    icon: 'warning',
                    confirmButtonColor: '#3085d6',
                });
                return;
            }
        }

        if (!uaRef.current || !uaRef.current.isConnected()) {
            console.error('UA not connected');
            return;
        }

        setCaller(number);
        const micId = selectedDevices.microphone === 'default' ? '' : selectedDevices.microphone;
        const stream = await getProcessedStream(micId, isNoiseCancellationEnabledRef.current);
        setCallEventId(eventId);
        uaRef.current.call(`sip:${number}@${config.domain}`, {
            mediaStream: stream,
        });
    }, [config.domain, micPermission, requestMicrophonePermission, getProcessedStream, selectedDevices.microphone]);

    const hangup = useCallback(() => { if (session) session.terminate(); }, [session]);

    const answer = useCallback(async () => {
        if (session && callStatus === 'incoming') {
            // Request mic permission first — must happen inside user-gesture call stack.
            if (micPermission !== 'granted') {
                const granted = await requestMicrophonePermission();
                if (!granted) {
                    Swal.fire({
                        title: 'Microphone Access Required',
                        text: 'Please allow microphone access in your browser to answer calls.',
                        icon: 'warning',
                        confirmButtonColor: '#3085d6',
                    });
                    return;
                }
            }

            const micId = selectedDevices.microphone === 'default' ? '' : selectedDevices.microphone;
            const stream = await getProcessedStream(micId, isNoiseCancellationEnabledRef.current);
            session.answer({ mediaStream: stream });
        }
    }, [session, callStatus, micPermission, requestMicrophonePermission, getProcessedStream, selectedDevices.microphone]);

    const reject = useCallback(() => {
        if (session && callStatus === 'incoming') session.terminate();
    }, [session, callStatus]);

    const hold = useCallback(() => {
        if (session && !session.isOnHold().local) {
            try { session.hold(); } catch (e) { console.error('Hold failed:', e); }
        }
    }, [session]);

    const unhold = useCallback(() => {
        if (session && session.isOnHold().local) {
            try { session.unhold(); } catch (e) { console.error('Unhold failed:', e); }
        }
    }, [session]);

    const mute = useCallback(() => { if (session) { session.mute(); setIsMuted(true); } }, [session]);
    const unmute = useCallback(() => { if (session) { session.unmute(); setIsMuted(false); } }, [session]);

    const sendDTMF = useCallback((tone) => {
        if (session && session.isEstablished()) {
            try { session.sendDTMF(tone); } catch (e) { console.error('DTMF failed:', e); }
        }
    }, [session]);

    const toggleNoiseCancellation = useCallback(() => {
        const newValue = !isNoiseCancellationEnabledRef.current;
        setIsNoiseCancellationEnabled(newValue);
        setNoiseCancellation(newValue);
    }, [setNoiseCancellation]);

    useEffect(() => {
        if (callStatus === 'active') {
            setCallDuration(0);
            timerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [callStatus]);

    // Once the user has confirmed a page refresh/close, end any live call (ringing,
    // dialing or answered) so it doesn't stay up on the server and the queue can
    // route the customer elsewhere. pagehide only fires after the native
    // "Leave site?" dialog is accepted, unlike beforeunload which fires before it.
    useEffect(() => {
        const onPageHide = () => {
            const s = sessionRef.current;
            if (s && !s.isEnded()) {
                try { s.terminate(); } catch (e) { console.error('Terminate on unload failed:', e); }
            }
        };
        window.addEventListener('pagehide', onPageHide);
        return () => window.removeEventListener('pagehide', onPageHide);
    }, []);


    const disconnect = useCallback(() => {
        if (uaRef.current) { uaRef.current.stop(); setStatus('disconnected'); }
    }, []);

    const unregister = useCallback(() => {
        if (uaRef.current && uaRef.current.isRegistered()) uaRef.current.unregister();
    }, []);
    
    const resetSip = useCallback(() => {
        if (uaRef.current) {
            uaRef.current.stop();
            uaRef.current = null;
        }
        sessionRef.current = null;
        setSession(null);
        setCallStatus('idle');
        setStatus('disconnected');
    }, []);

    const reconnectSip = useCallback((overrideConfig) => {
        const doConnect = () => {
            sessionRef.current = null;
            setSession(null);
            setCallStatus('idle');
            if (overrideConfig) setConfig(overrideConfig);
            connect();
        };

        if (uaRef.current) {
            const oldUa = uaRef.current;
            uaRef.current = null;

            // wait for the old UA to actually disconnect before building a new one
            oldUa.on('disconnected', doConnect);
            oldUa.stop();

            // safety net in case 'disconnected' never fires
            setTimeout(() => {
                if (uaRef.current === null) doConnect();
            }, 800);
        } else {
            doConnect();
        }
    }, [connect]);
    
    const value = {
        status, callStatus, caller, callerNumber, isHeld, isMuted, remoteAudioRef,
        eventId: callEventId,
        campaignCallInfo, setCampaignCallInfo,
        config, updateConfig, connect, disconnect, unregister,
        makeCall, hangup, answer, reject, hold, unhold, mute, unmute,
        sendDTMF, callDuration,
        isDialerOpen, openDialer, closeDialer,
        audioDevices,
        selectedDevices,
        changeMicrophone,
        changeSpeaker,
        isNoiseCancellationEnabled,
        toggleNoiseCancellation,
        disableSip,
        micPermission,
        permissionError,
        checkMicrophonePermission,
        requestMicrophonePermission,
        resetSip,
        reconnectSip,
        setCallStatus,
        setCaller,
        setSession
    };

    const [audioReady, setAudioReady] = useState(false);

    // SIPContext.js (code for making a refresh effect, to fix the incoming call popup not showing issue, while agent call mode switches from mobile to sip, on call , by admin)

    return (
        <SIPContext.Provider value={value}>
            <audio
                ref={(el) => {
                    remoteAudioRef.current = el;
                    if (el && !audioReady) setAudioReady(true);
                }}
                autoPlay
            />
            {audioReady && children}
        </SIPContext.Provider>
    );
};

export const useSIP = () => {
    const context = useContext(SIPContext);
    if (!context) throw new Error('useSIP must be used within SIPProvider');
    return context;
};
